import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Field = ({ id, label, ...props }) => (
  <label className="block" htmlFor={id}>
    <span className="eyebrow">{label}</span>
    <input id={id} {...props} className="mt-2 w-full border-b border-ink/40 bg-transparent py-3 text-lg outline-none transition-colors focus:border-ink" />
  </label>
);

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        setNotice("If that email has an account, a reset link is on its way. Check your inbox (and spam folder).");
      } else {
        const body = mode === "login" ? { email: form.email, password: form.password } : form;
        const { data } = await api.post(`/auth/${mode}`, body);
        setUser(data);
        navigate(data.role === "admin" ? "/admin" : "/account");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/account")}`;
    const win = window.open(authUrl, "_blank");
    if (!win) window.location.href = authUrl;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const titles = { login: <>Sign <em>in.</em></>, register: <>Create <em>account.</em></>, forgot: <>Reset <em>password.</em></> };

  return (
    <main data-testid="login-page" className="grid min-h-screen md:grid-cols-2">
      <div className="relative hidden overflow-hidden md:block">
        <img src="/img/coat-shades2.jpg" alt="goencho editorial" className="absolute inset-0 h-full w-full object-cover" />
      </div>
      <div className="flex flex-col justify-center px-5 pb-20 pt-28 md:px-16">
        <p className="eyebrow">{mode === "login" ? "Welcome back" : mode === "register" ? "New here" : "Password help"}</p>
        <h1 className="mt-4 font-display text-6xl font-light leading-none">{titles[mode]}</h1>
        <form onSubmit={submit} className="mt-10 max-w-md space-y-6">
          {mode === "register" && <Field id="name" label="Name" required value={form.name} onChange={set("name")} data-testid="auth-name-input" />}
          <Field id="email" label="Email" type="email" required value={form.email} onChange={set("email")} data-testid="auth-email-input" />
          {mode !== "forgot" && (
            <Field id="password" label="Password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="auth-password-input" />
          )}
          {notice && <p data-testid="auth-notice" className="text-sm text-green-700">{notice}</p>}
          {error && <p data-testid="auth-error" className="text-sm text-signal">{error}</p>}
          {mode === "forgot" ? (
            <button type="submit" disabled={loading} data-testid="auth-submit-button" className="w-full rounded-full bg-ink py-4 text-sm text-bone transition-colors duration-300 hover:bg-signal disabled:opacity-60">
              {loading ? "Sending…" : "Send reset link"}
            </button>
          ) : (
            <button type="submit" disabled={loading} data-testid="auth-submit-button" className="w-full rounded-full bg-ink py-4 text-sm text-bone transition-colors duration-300 hover:bg-signal disabled:opacity-60">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          )}
          {mode !== "forgot" && (
            <button type="button" onClick={google} data-testid="auth-google-button" className="flex w-full items-center justify-center gap-3 rounded-full border border-ink py-4 text-sm transition-colors duration-300 hover:bg-sand">
              <svg viewBox="0 0 48 48" className="h-4 w-4"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
              Continue with Google
            </button>
          )}
        </form>
        {mode === "login" && (
          <button data-testid="auth-forgot-button" onClick={() => { setMode("forgot"); setError(""); setNotice(""); }} className="link-underline mt-6 w-fit text-sm text-stone">
            Forgot password?
          </button>
        )}
        <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "forgot" ? "login" : mode === "login" ? "register" : "login"); setError(""); setNotice(""); }} className="link-underline mt-4 w-fit text-sm">
          {mode === "login" ? "No account? Create one" : "Back to sign in"}
        </button>
      </div>
    </main>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const hash = window.location.hash;
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        setUser(data);
        navigate("/account", { replace: true, state: { user: data } });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [hash, navigate, setUser]);

  return <main data-testid="auth-callback" className="min-h-screen" />;
}
